import { Box, Button, CircularProgress } from '@mui/material';
import { usePost } from 'hooks/use-post';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import _ from 'underscore';

import Collapsible from 'components/collapsible';
import LoadingSpinner from 'components/loading-spinner';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';
import RegistrationHeader from 'views/production-systems/components/registration-header';
import ListFeeders from 'views/production-systems/features/farm-setup/components/list-feeders';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePondsState } from 'views/production-systems/context/ponds-context';

import { postPondStatusFeeders } from 'views/production-systems/features/farm-setup/api/get-pond-feeder-status';
import { putFeeders } from 'views/production-systems/features/farm-setup/api/put-feeder';

import { Feeder, PondFeederAssignments, PondsWithFeeders } from 'views/production-systems/features/farm-setup/types/feeders.type';
import { Pond } from 'views/production-systems/features/farm-setup/types/pond.type';
import {
  PondFeederAssigments,
  UpdateAssignedFeeders,
  UpdateAssignedFeedersInput,
  UpdateFeedersInput,
  UpdateFeedersInputForm,
} from 'views/production-systems/features/farm-setup/types/update-feeders-input.type';
import { FeedersProvider } from 'views/self-service/features/feeders/context/feeders-context';

type ParamTypes = {
  globalSiteId: string;
};

type Props = {
  ponds: Pond[];
  handleClose: () => void;
};

const ManageFeeders: React.FC<Props> = ({ ponds, handleClose }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const params = useParams<ParamTypes>();
  const snackbarDispatch = useSnackbarDispatch();

  const [confirm, setConfirm] = useState<boolean>(false);
  const [pondFeederAssignments, setPondFeederAssignments] = useState<PondFeederAssignments[]>();

  // #region HOOKS
  const pondsWithFeeders = useMemo(() => {
    return _.chain(pondFeederAssignments)
      .sortBy('pondName')
      .value()
      ?.map((pond) => {
        return {
          pondId: pond.pondId,
          pondName: pond.pondName,
          pondCurrentBiomass: pond.pondCurrentBiomass,
          assignedFeeders: pond.assignedFeeders
            ?.filter((feeder) => feeder.feederId)
            .map((feeder) => {
              return {
                feederId: feeder.feederId,
                feederName: feeder.feederName,
                feederBrand: feeder.feederBrand,
                storageCapacity: feeder.storageCapacity,
                feedingCapacity: feeder.feedingCapacity,
                numberOfFeeders: feeder.numberOfFeeders,
                feederRegDate: feeder.feederRegDate,
                edit: false,
                isDeleted: feeder.numberOfFeeders === 0,
              };
            }),
        };
      }) as UpdateFeedersInput[];
  }, [ponds, pondFeederAssignments]);

  const { lockedPonds, registrationSetupDate: registrationDate } = usePondsState();
  // #endregion

  // #region FORMS
  const defaultValues = {
    ponds: pondsWithFeeders,
  };

  const form = useForm<UpdateFeedersInputForm>({
    defaultValues: defaultValues,
    shouldUnregister: false,
    mode: 'onChange',
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  const { fields: rows } = useFieldArray({
    control: control,
    name: `ponds`,
  });

  const watchPonds = useWatch({ control, name: 'ponds' }) || [];
  // #endregion

  //#region POST DATA

  const { isPending: isPondsLoading, mutate: getPondsWithAssignedFeeders } = usePost<
    PondsWithFeeders,
    { globalSiteId: string; statusDate: string; pondIds: string[] }
  >(
    (request) => postPondStatusFeeders(request.globalSiteId, request.statusDate, request.pondIds),
    (data) => {
      if (data) {
        const pondFeederAssignments = data.pondFeederAssignments.map((pond) => pond);
        setPondFeederAssignments(pondFeederAssignments);
      }
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const { isPending: isAssignLoading, mutate: assignFeedersToPonds } = usePost<
    Feeder,
    { globalSiteId: string; request: UpdateAssignedFeedersInput }
  >(
    (request) => putFeeders(request.globalSiteId, request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      handleClose();
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion POST DATA

  // #region HANDLERS
  const handleGetPondsData = (globalSiteId: string, statusDate: string) => {
    getPondsWithAssignedFeeders({
      globalSiteId: globalSiteId,
      statusDate: statusDate,
      pondIds: ponds.map((pond) => pond.pondId),
    });
  };

  const handleSubmitFeedersData = (request: { ponds: UpdateFeedersInput[] }) => {
    const assignments: PondFeederAssigments[] = request.ponds
      .map((pond) => {
        const feedersForPond = pond.assignedFeeders.reduce((acc: UpdateAssignedFeeders[], feeder) => {
          // Somewhere here we are adding feeders to ponds that are not necessary. They are zero'd out, but I noticed when logging that even when a pond had no feeders assigned to it, we are still passing a feeder id and quantity 0 in the payload. Where and why this is included in the list, i do not know.

          // This behavior is the same for the submit in edit-feeder, edit-aerator, and manage-aerators components.
          if (!feeder.isDeleted) {
            acc.push({
              feederId: feeder.feederId,
              quantity: Number(feeder.numberOfFeeders),
            });
          } else if (feeder.isDeleted && !feeder.isNew) {
            acc.push({
              feederId: feeder.feederId,
              quantity: 0,
            });
          }
          return acc;
        }, []);

        return {
          pondId: pond.pondId,
          assignedFeeders: feedersForPond,
        };
      })
      .filter((pond) => pond.assignedFeeders.length > 0)
      .map((pond) => {
        return {
          pondId: pond.pondId,
          assignedFeeders: pond.assignedFeeders.map((feeder) => ({ feederId: feeder.feederId, quantity: Number(feeder.quantity) })),
        } as PondFeederAssigments;
      });

    const pondsWithAssignments: UpdateAssignedFeedersInput = {
      date: registrationDate.format('YYYY-MM-DD'),
      pondFeederAssignments: assignments,
    };

    if (pondsWithAssignments.pondFeederAssignments.length === 0) return;

    assignFeedersToPonds({ globalSiteId: params.globalSiteId, request: pondsWithAssignments });
  };

  const isDisableSubmit = watchPonds
    .flatMap((pond) => pond.assignedFeeders || [])
    .every((aerator) => aerator.isNew && aerator.isDeleted);
  // #endregion

  useEffect(() => {
    const statusDate = registrationDate.format('YYYY-MM-DD').toString();
    handleGetPondsData(params.globalSiteId, statusDate);
  }, []);

  useEffect(() => {
    reset({ ponds: pondsWithFeeders });
  }, [pondsWithFeeders]);

  return isPondsLoading ? (
    <LoadingSpinner />
  ) : lockedPonds.notGranted.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <>
      <PondLockTimer handleDialogClose={handleClose} />
      <RegistrationHeader dateCreated={registrationDate} />
      <FormProvider {...form}>
        <form onSubmit={handleSubmit((data) => handleSubmitFeedersData(data))}>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            {rows.map((pond, idx) => {
              const { pondCurrentBiomass, pondName } = pond;
              return (
                <Collapsible hideHeaderBorder label={`${pondName}`} open={idx === 0}>
                  <ListFeeders fieldIndex={idx} pondCurrentBiomass={pondCurrentBiomass} />
                </Collapsible>
              );
            })}
            <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
              <Button
                type="submit"
                disabled={isAssignLoading || !isValid || !isDirty || isDisableSubmit}
                startIcon={isAssignLoading ? <CircularProgress color="inherit" size={18} /> : null}>
                <FormattedMessage id="general.submit" values={{ type: '' }} />
              </Button>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </>
  );
};

const ManageFeedersContainer: React.FC<Props> = ({ ponds, handleClose }) => {
  return (
    <FeedersProvider>
      <ManageFeeders ponds={ponds} handleClose={handleClose} />
    </FeedersProvider>
  );
};

export default React.memo(ManageFeedersContainer);
